import type { ICatalystHandInContext, IContextualChat } from '@1js/catalyst';
import type { ContentItemType } from '@1js/scc-types';
import { create } from 'zustand';

import type { GlobalCopilotContextData } from './types';

const catalystAtdVariant = '-agt_module_atd_v2';

interface CatalystSDKState {
  readonly hostGeneration: number;
  readonly hostLifecycle: CatalystHostLifecycle;
  readonly nextHandIn: ICatalystHandInContext | null;
}

export type CatalystHostLifecycle = 'failed' | 'idle' | 'warming' | 'ready';

const useCatalystStore = create<CatalystSDKState>(() => ({
  hostGeneration: 0,
  hostLifecycle: 'idle',
  nextHandIn: null,
}));

export const requestCatalystPrewarm = (): void => {
  if (useCatalystStore.getState().hostLifecycle === 'idle') {
    useCatalystStore.setState({ hostLifecycle: 'warming' });
  }
};

export const markCatalystReady = (): void => {
  useCatalystStore.setState({ hostLifecycle: 'ready' });
};

export const markCatalystFailed = (): void => {
  useCatalystStore.setState({ hostLifecycle: 'failed' });
};

export const retryCatalystIfFailed = (): void => {
  if (useCatalystStore.getState().hostLifecycle === 'failed') {
    useCatalystStore.setState((state) => ({
      hostGeneration: state.hostGeneration + 1,
      hostLifecycle: 'warming',
    }));
  }
};

export const resetCatalystHostLifecycle = (): void => {
  useCatalystStore.setState({ hostGeneration: 0, hostLifecycle: 'idle' });
};

export const useCatalystHostLifecycle = (): CatalystHostLifecycle => useCatalystStore((state) => state.hostLifecycle);

export const getCatalystHostLifecycle = (): CatalystHostLifecycle => useCatalystStore.getState().hostLifecycle;

export const useCatalystHostGeneration = (): number => useCatalystStore((state) => state.hostGeneration);

export const buildVivaEngageContentItems = (
  context: GlobalCopilotContextData
): ICatalystHandInContext['contentItems'] => {
  const contentItem = { contentType: 'vivaEngage' as ContentItemType.VIVAENGAGE, ...context };
  delete contentItem.supportedScenario;

  return [contentItem];
};

export const buildVivaEngageDataContext = (context: GlobalCopilotContextData): IContextualChat => {
  const contentItems = buildVivaEngageContentItems(context);
  const dataContext = {
    contentType: 'vivaEngage' as ContentItemType.VIVAENGAGE,
    contentItems,
    contextAttributes: [{ key: 'SupportedScenario', value: context.supportedScenario ?? 'ViewHome' }],
    serverVariants: [catalystAtdVariant],
  };

  return dataContext;
};

export const setNextHandIn = (promptText: string, context: GlobalCopilotContextData): void => {
  const nextHandIn = {
    ...buildVivaEngageDataContext(context),
    promptText,
    isNewChat: true,
  };

  useCatalystStore.setState({
    nextHandIn,
  });
};

export const clearNextHandIn = (): void => {
  useCatalystStore.setState({ nextHandIn: null });
};

export const useNextHandIn = (): ICatalystHandInContext | null => useCatalystStore((state) => state.nextHandIn);

export const getNextHandIn = (): ICatalystHandInContext | null => useCatalystStore.getState().nextHandIn;
